import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function PaymentStatus() {
  const [sp] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const orderId = sp.get("order");
  const status = sp.get("status");
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    // Same as PaymentTestSimulator: this page is landed on via a full-page redirect
    // (iyzico callback), so wait for AuthProvider's own /auth/me check to finish
    // before treating a momentarily-null user as "logged out".
    if (authLoading) return;
    if (!user) { nav("/giris"); return; }
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`${V1}/payments/iyzico/order/${orderId}`);
        if (mounted) setPayment(data);
      } catch { /* ignore */ }
      finally { if (mounted) setLoading(false); }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { mounted = false; clearInterval(t); };
  }, [orderId, user, authLoading, nav]);

  const ok = status === "success" || payment?.status === "paid";
  const failed = status === "failed" || payment?.status === "failed";
  const pending = !ok && !failed;

  const downloadInvoice = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/payments/${orderId}/invoice.pdf`;
    const token = localStorage.getItem("sano_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = `fatura-${orderId.slice(0, 12)}.pdf`;
        link.click();
      }).catch(() => toast.error("Fatura indirilemedi"));
  };

  const submitRefund = async () => {
    if (refundReason.trim().length < 5) { toast.error("Sebep en az 5 karakter"); return; }
    try {
      await api.post(`${V1}/refund-requests`, { payment_id: orderId, reason: refundReason.trim() });
      toast.success("İade talebiniz alındı, en kısa sürede incelenecek");
      setRefundOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  if (loading && !payment) return <div className="max-w-lg mx-auto py-24 text-center text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="max-w-lg mx-auto py-16 px-4" data-testid="payment-status-page">
      <div className="p-8 rounded-2xl border border-border bg-card text-center">
        {ok && (<><CheckCircle2 className="h-16 w-16 text-success mx-auto mb-4" /><h1 className="font-display text-3xl font-bold" data-testid="payment-success">Ödemeniz Alındı!</h1><p className="text-muted-foreground mt-2">{payment?.kind === "plan" ? "Aboneliğiniz aktifleştirildi." : payment?.kind === "promote_exam" ? "Sınavınız vitrine alındı." : "Modülünüz aktifleştirildi."}</p></>)}
        {failed && (<><XCircle className="h-16 w-16 text-destructive mx-auto mb-4" /><h1 className="font-display text-3xl font-bold" data-testid="payment-failed">Ödeme Başarısız</h1></>)}
        {pending && (<><Loader2 className="h-16 w-16 text-accent animate-spin mx-auto mb-4" /><h1 className="font-display text-3xl font-bold">İşleniyor…</h1></>)}

        {payment && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Sipariş No:</span><b className="font-mono">{payment.id.slice(0, 12)}…</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Ürün:</span><b>{payment.item_name || payment.code}</b></div>
            {payment.coupon_line && (
              <div className="flex justify-between text-success"><span>Kupon ({payment.coupon_line.code}):</span><b>-₺{payment.coupon_line.discount}</b></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Tutar:</span><b>₺{payment.amount}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Durum:</span><b>{payment.status}</b></div>
          </div>
        )}

        <div className="mt-8 flex gap-2 justify-center flex-wrap">
          {ok && (
            <Button variant="outline" onClick={downloadInvoice} data-testid="download-invoice-btn">
              <Download className="h-4 w-4" /> Faturayı İndir
            </Button>
          )}
          {ok && payment?.status !== "refunded" && (
            <Button variant="outline" onClick={() => setRefundOpen(true)} data-testid="request-refund-btn">
              <RotateCcw className="h-4 w-4" /> İade Talebi
            </Button>
          )}
          {ok && user?.role === "school" && (
            <Button asChild><Link to="/okul-paneli/modullerim">Modüllerim <ArrowRight className="h-4 w-4" /></Link></Button>
          )}
          {ok && user?.role !== "school" && (
            <Button asChild><Link to="/panel/aboneligim">Aboneliğim <ArrowRight className="h-4 w-4" /></Link></Button>
          )}
          {(failed || pending) && (
            <Button asChild><Link to="/">Ana Sayfa</Link></Button>
          )}
        </div>
      </div>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent><DialogHeader><DialogTitle>İade Talebi</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm text-muted-foreground mb-3">İade koşullarımıza göre talebiniz 14 iş günü içinde incelenir. Sebebinizi kısaca yazın:</p>
            <Textarea rows={4} value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Örn: Yanlış plan aldım, iptal etmek istiyorum." data-testid="refund-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Vazgeç</Button>
            <Button onClick={submitRefund} data-testid="submit-refund-btn">Talep Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
